import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'astroturf/react'
import { parseQuery } from '@calendly/http-tools'
import { nanoid } from 'nanoid'
import { uniqBy } from 'lodash-es'
import { Button } from '@calendly/ui/components/button'
import { Recaptcha } from '@calendly/recaptcha'
import { Segment } from '@calendly/segment'
import { useNewCode } from '@calendly/feature-check-react'

import { useValidateCouponMutation, eventTypeSelector } from 'root-api'
import { useI18n } from 'locales'
import { buildAnswers } from 'services/answers'
import { buildGuests } from 'services/guests'
import {
  isPollSelector,
  locationConfigurationsSelector,
  ignoreLocationConfigurationsSelector,
  showMailchimpInviteeConsentSelector
} from 'selectors/event_type'
import {
  inviteeGuestsErrorsSelector,
  inviteeInvalidGuestEmailsSelector,
  inviteeNameFormatSelector,
  schedulingSelector
} from 'selectors'
import { currentInviteeSelector } from 'selectors/current_invitee'
import {
  embedSelector,
  showRecaptchaChallengeSelector,
  failedRecaptchaChallengeSelector,
  recaptchaRequestIdSelector,
  autofillDataSelector,
  customFieldsSelector,
  isBookingAfterStripeRedirectSelector
} from 'selectors/scheduling'
import { isGdprAppliedSelector } from 'selectors/gdpr'
import {
  formFieldUpdated,
  formUnmounted,
  submitPollVotes,
  submitScheduling,
  resumeAsyncBooking,
  saveStripeRedirectSession,
  dismissRecaptchaChallenge,
  setInitialCustomField,
  updateCustomField,
} from 'actions/scheduling'
import { dismissScheduleError, dismissValidationError, setScheduleError } from 'actions/errors'
import { isMultipleLocation, isWithoutLocation } from 'services/location'
import { buildRequestId, buildRequestIdBase64 } from 'services/request_id'
import { buildNewInvitee } from 'services/invitee'
import { subscribeEmbedPrefill } from 'services/embed_prefill_handoff'
import MultipleLocation from 'components/location/multiple'
import SingleLocation from 'components/location/single'
import CustomFields from 'components/custom_fields'
import TextReminder from 'components/text_reminder'

import GuestsField from '../../fields/guest'
import BookingField from '../../booking_field'
import Payment from '../../payment'
import IntercomConfirmation from '../../intercom_confirmation'
import { ExternalCommunicationConsent } from '../../fields/external_communication_consent'
import RecaptchaPopup from '../../recaptcha_popup'
import {
  isPaymentCheckoutAvailable,
  saveFormDataForStripeRedirectSelector,
  stripeClientSecretSelector
} from '../../../../selectors/payment'
import { usePurchasedPackageData } from '../../../../hooks/usePurchasedPackageData'
import { eventService } from 'services/analytics/event_service'

import InviteeInputs from './invitee_inputs'
import LegalCopy from './legal_copy'
import _Container from "index-Container.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/enter_details/booking_form/scheduling_form_feature/index.js?styleId=Container";


const Container = /*#__PURE__*/styled('div', null, {
  displayName: "Container",
  styles: _Container
})

/** When embed uses custom colors, initTheme maps page tokens to the embed palette; restore white form panel (production). */
const embedCustomColorFormSurfaceStyle = {
  '--color-bg-white-l-1': '#ffffff',
  '--colorBackgroundStandard': '#ffffff',
}

const SchedulingForm = ({ spotStartTimes, spotStartTime, requestKeySalt, profile, asyncBookingEnabled = false }) => {
  const { i18n } = useI18n()
  const { client, platform, booking_flow: bookingFlow, creator_uuid: creatorUuid } = parseQuery(window.location.search)
  const currentClient = client ? { client, platform } : false

  const getBookingRequestId = () => {
    const params = new URLSearchParams(window.location.search)
    return params.get('br_id')
  }

  const eventType = useSelector(eventTypeSelector)
  const {
    custom_fields: customFields,
    guests_allowed: guestsAllowed,
    sms_reminder_enabled: smsReminderEnabled,
    uuid,
    id: eventTypeId,
    owning_user: owningUser,
    organization,
  } = eventType
  const autofillData = useSelector(autofillDataSelector)
  const currentInvitee = useSelector(currentInviteeSelector)
  const predefinedAnswers = buildAnswers()
  const predefinedGuests = buildGuests()
  const guestErrors = useSelector(inviteeGuestsErrorsSelector)
  const invalidGuestEmails = useSelector(inviteeInvalidGuestEmailsSelector)
  const locationConfigurations = useSelector(locationConfigurationsSelector)
  const ignoreLocationConfigurations = useSelector(ignoreLocationConfigurationsSelector)
  const isPoll = useSelector(isPollSelector)
  const { status, is_submitting_intercom: isSubmittingIntercomSheet } = useSelector(schedulingSelector)
  const isGdprApplied = useSelector(isGdprAppliedSelector)
  const showRecaptchaChallenge = useSelector(showRecaptchaChallengeSelector)
  const failedRecaptchaChallenge = useSelector(failedRecaptchaChallengeSelector)
  const recaptchaRequestId = useSelector(recaptchaRequestIdSelector)
  const showMailchimpInviteeConsent = useSelector(showMailchimpInviteeConsentSelector)
  const eventCustomFields = useSelector(customFieldsSelector)
  const dispatch = useDispatch()
  const { color_customization: embedCustomColors } = useSelector(embedSelector)
  const saveFormDataForStripeRedirect = useSelector(saveFormDataForStripeRedirectSelector)
  const stripeClientSecret = useSelector(stripeClientSecretSelector)
  const { purchasedPackage } = usePurchasedPackageData(uuid)
  const submitButton = useRef()
  const automaticallySubmitForStripeRedirect = useSelector(isBookingAfterStripeRedirectSelector)
  const hasResumedAsyncBooking = useRef(false)

  const inviteeNameFormat = useSelector(inviteeNameFormatSelector)
  const telemetryId = useSelector((state) => state.tracking?.stytch_telemetry_id)

  const [, { data: couponData }
  ] = useValidateCouponMutation({
    fixedCacheKey: 'couponData'
  })

  const getInviteeValue = useCallback(
    (attribute) => {
      const newInvitee = buildNewInvitee()
      newInvitee.email = purchasedPackage?.purchaser_email || newInvitee.email
      newInvitee.full_name = purchasedPackage?.purchaser_name || newInvitee.full_name

      return newInvitee[attribute] || (currentInvitee || {})[attribute] || autofillData[attribute] || ''
    },
    [purchasedPackage?.purchaser_email, purchasedPackage?.purchaser_name, currentInvitee, autofillData]
  )

  const buildInviteeAttribute = useCallback((attribute, required) => {
    const value = getInviteeValue(attribute)
    dispatch(formFieldUpdated(attribute, value))

    return {
      name: attribute,
      required,
      value,
    }
  }, [dispatch, getInviteeValue])

  const buildInviteeName = useMemo(() => {
    const name = {}
    switch (inviteeNameFormat) {
      case 'separated':
        name.first_name = buildInviteeAttribute('first_name', true)
        name.last_name = buildInviteeAttribute('last_name', true)
        name.full_name = { value: null }
        break
      case 'combined':
        name.full_name = buildInviteeAttribute('full_name', true)
        name.first_name = { value: null }
        name.last_name = { value: null }
        break
      default:
        break
    }
    return name
  }, [inviteeNameFormat, buildInviteeAttribute])

  const buildInviteEmail = () => (buildInviteeAttribute('email', true))

  const buildEventFieldsState = useCallback(() => customFields.map((field) => ({
    ...field,
    value: predefinedAnswers[field.position + 1] || '',
  })), [customFields, predefinedAnswers])

  const buildInviteePhoneNumber = useCallback(() => (smsReminderEnabled
    ? buildInviteeAttribute('phone_number', false) : null),
  [smsReminderEnabled, buildInviteeAttribute])

  const buildInviteeGuest = useCallback((email) => ({ id: nanoid(5), email }), [])

  const buildInitialInviteeGuest = useMemo(() => predefinedGuests.map(buildInviteeGuest), [predefinedGuests, buildInviteeGuest])

  const buildExternalCommunicationConsent = useMemo(() => {
    // It's important for us to differentiate true false and null values here.
    // null means consent checkbox was never shown for GDPR user
    if (isGdprApplied) {
      if (showMailchimpInviteeConsent) {
        return false
      }
    } else if (showMailchimpInviteeConsent) {
      return true
    }
    return null
  }, [isGdprApplied, showMailchimpInviteeConsent])

  const [recaptchaTokenV3, setRecaptchaTokenV3] = useState('')
  const [recaptchaTokenV2, setRecaptchaTokenV2] = useState(undefined)
  const [inviteeName, setInviteeName] = useState(buildInviteeName)
  const [inviteeEmail, setInviteeEmail] = useState(buildInviteEmail)
  const [inviteeGuests, setInviteeGuests] = useState(buildInitialInviteeGuest)
  const [inviteePhoneNumber, setInviteePhoneNumber] = useState(buildInviteePhoneNumber)
  const [event, setEvent] = useState({ location: '', guest_emails: predefinedGuests })
  const [paymentApi, setPaymentApi] = useState(null)
  const [externalCommunicationConsent, setExternalCommunicationConsent] = useState(buildExternalCommunicationConsent)

  const showLegalCopy = useNewCode('legal_copy_invitee_experience')?.enabled

  const embedPrefillHandlerContextRef = useRef({
    inviteeNameFormat,
    guestsAllowed,
    customFields,
  })
  embedPrefillHandlerContextRef.current = {
    inviteeNameFormat,
    guestsAllowed,
    customFields,
  }

  useEffect(() => {
    const requestIdFromUrl = getBookingRequestId()
    if (!requestIdFromUrl) {
      dispatch(dismissScheduleError())
    }
    dispatch(formUnmounted())
  }, [dispatch])

  // Runs before subscribeEmbedPrefill so a synchronous replay cannot populate Redux first and
  // then lose data when this effect still sees eventCustomFields.length === 0 from this render.
  useEffect(() => {
    if (customFields.length !== 0 && eventCustomFields.length === 0) {
      dispatch(setInitialCustomField(buildEventFieldsState()))
    }
  }, [eventCustomFields, dispatch, customFields, buildEventFieldsState])

  useEffect(() => {
    return subscribeEmbedPrefill((p) => {
      if (!p) return

      const {
        inviteeNameFormat: nameFormat,
        guestsAllowed: allowGuests,
        customFields: fields,
      } = embedPrefillHandlerContextRef.current

      if (p.email) {
        setInviteeEmail((prev) => ({ ...prev, value: p.email }))
        dispatch(formFieldUpdated('email', p.email))
      }

      if (nameFormat === 'separated') {
        setInviteeName((prev) => ({
          ...prev,
          first_name: { ...prev.first_name, value: p.first_name || prev.first_name.value },
          last_name: { ...prev.last_name, value: p.last_name || prev.last_name.value },
        }))
        if (p.first_name) dispatch(formFieldUpdated('first_name', p.first_name))
        if (p.last_name) dispatch(formFieldUpdated('last_name', p.last_name))
      } else if (nameFormat === 'combined' && p.full_name) {
        setInviteeName((prev) => ({
          ...prev,
          full_name: { ...prev.full_name, value: p.full_name },
        }))
        dispatch(formFieldUpdated('full_name', p.full_name))
      }

      if (allowGuests && p.guests?.length > 0) {
        setInviteeGuests(p.guests.map(buildInviteeGuest))
      }

      Object.entries(p.customAnswers).forEach(([key, value]) => {
        const m = key.match(/^a(\d+)$/)
        if (!m || value == null) return
        const position = parseInt(m[1], 10)
        const index = fields.findIndex((f) => f.position + 1 === position)
        if (index >= 0) {
          dispatch(formFieldUpdated(`question_${index}`, value))
          dispatch(updateCustomField({ index, props: { value } }))
        }
      })
    })
  }, [buildInviteeGuest, dispatch])

  useEffect(() => {
    switch (inviteeNameFormat) {
      case 'separated':
        if (
          !inviteeEmail.value
          && !inviteeName.first_name.value
          && !inviteeName.last_name.value
          && Object.keys(autofillData
          ).length !== 0) {
          setInviteeEmail((prevInviteeEmail) => ({
            ...prevInviteeEmail,
            value: autofillData.email
          }))

          setInviteeName((prevInviteeName) => ({
            ...prevInviteeName,
            first_name: {
              ...prevInviteeName.first_name,
              value: autofillData.first_name
            },
            last_name: {
              ...prevInviteeName.last_name,
              value: autofillData.last_name
            }
          }))
        }
        break
      case 'combined':
        if (!inviteeEmail.value && !inviteeName.full_name.value && Object.keys(autofillData).length !== 0) {
          setInviteeEmail((prevInviteeEmail) => ({
            ...prevInviteeEmail,
            value: autofillData.email
          }))

          setInviteeName((prevInviteeName) => ({
            ...prevInviteeName,
            full_name: {
              ...prevInviteeName.full_name,
              value: autofillData.full_name
            }
          }))
        }
        break
      default:
        break
    }
  }, [
    inviteeNameFormat,
    inviteeEmail.value,
    inviteeName.first_name.value,
    inviteeName.last_name.value,
    inviteeName.full_name.value,
    autofillData
  ])

  const recaptchaHandler = useCallback((callback) => {
    setRecaptchaTokenV3(callback)
  }, [])

  const onInviteeNameChange = (e) => {
    if (e.target.name === 'full_name') {
      setInviteeName({
        ...inviteeName,
        full_name: {
          ...inviteeName.full_name,
          value: e.target.value
        }
      })
    } else if (e.target.name === 'first_name') {
      setInviteeName({
        ...inviteeName,
        first_name: {
          ...inviteeName.first_name,
          value: e.target.value
        }
      })
    } else if (e.target.name === 'last_name') {
      setInviteeName({
        ...inviteeName,
        last_name: {
          ...inviteeName.last_name,
          value: e.target.value
        }
      })

      dispatch(formFieldUpdated(e.target.name, e.target.value))
    }
  }

  const onInviteEmailChange = (e) => {
    if (e.target.name === 'email') {
      setInviteeEmail((prevInviteeEmail) => ({
        ...prevInviteeEmail,
        value: e.target.value
      }))
    }
    dispatch(formFieldUpdated(e.target.name, e.target.value))
  }

  const onGuestsChange = useCallback(() => {
    const emails = inviteeGuests.map(({ email }) => email)
    const uniqEmails = uniqBy(emails, (e) => e.toLocaleLowerCase())

    setEvent((prevEvent) => ({
      ...prevEvent,
      guest_emails: uniqEmails
    }))
  }, [inviteeGuests])

  const onGuestsAdd = (email) => {
    setInviteeGuests((prevInviteeGuests) => [...prevInviteeGuests, buildInviteeGuest(email)])
  }

  const onGuestsRemove = (guestId) => {
    setInviteeGuests(inviteeGuests.filter(({ id }) => id !== guestId))
  }

  const onGuestsUpdate = (guestId, newEmail) => {
    if (!newEmail) {
      onGuestsRemove(guestId)
      return
    }

    setInviteeGuests(inviteeGuests.map(({ id, email }) => ({
      id,
      email: id === guestId ? newEmail : email
    })))
  }

  useEffect(() => {
    onGuestsChange()
  }, [inviteeGuests, onGuestsChange])


  const onCustomFieldChange = useCallback((index, props) => {
    const value = props.value ? props.value : ''
    dispatch(formFieldUpdated(`question_${index}`, value))
    const updatedCustomFieldData = {
      index,
      props
    }
    dispatch(updateCustomField(updatedCustomFieldData))
  }, [dispatch])

  const onTextReminderChange = useCallback((phoneNumberInfo) => {
    dispatch(formFieldUpdated('phone_number', phoneNumberInfo.value))
    setInviteePhoneNumber((prevInviteePhoneState) => ({
      ...phoneNumberInfo,
      ...prevInviteePhoneState,
      isValid: phoneNumberInfo.isValid,
      value: phoneNumberInfo.value
    }
    ))
  }, [dispatch])

  const registerApi = useCallback((api) => {
    setPaymentApi(api)
  }, [])

  const dispatchRecaptchaInteractionAnalyticsCallback = (action) => {
    eventService.dispatch({
      name: 'ReCaptcha Interaction',
      version: 2,
      dependencies: {
        user_uuid: owningUser.uuid,
        organization_uuid: organization.uuid
      },
      entities: ['user', 'organization'],
      properties: {
        location: 'booking_page',
        action,
        event_type_id: eventTypeId,
        event_type_uuid: uuid,
        stytch_telemetry_id: telemetryId
      }
    })
  }

  // may need a use effect
  const dismissRecaptchaPopup = () => {
    setRecaptchaTokenV2(null)
    dispatch(dismissRecaptchaChallenge())
  }

  const recaptchaEnqueueAnalytic = useCallback((type) => {
    Segment.track('abuse_prevention.recaptcha.token_generation', {
      action: 'scheduling',
      type
    })
  }, [])

  const closeRecaptchaModalAnalytic = () => {
    Segment.track('abuse_prevention.invitee.recaptcha_modal_closed', {
      publisher_id: profile?.id
    })
  }

  const unfinishedRecaptchaModalAnalytic = () => {
    if ((typeof recaptchaTokenV2 === 'undefined') && showRecaptchaChallenge) {
      Segment.track('abuse_prevention.invitee.unfinished_recaptcha_continue', {
        publisher_id: profile?.id
      })
    }
  }

  const dismissGuestsErrors = () => {
    dispatch(dismissValidationError('event', 'event_guests'))
    return dispatch(dismissValidationError('event', 'event_guests_emails'))
  }

  const inviteeNameValue = () => {
    if (inviteeNameFormat === 'separated') {
      return `${inviteeName.first_name.value}|${inviteeName.last_name.value}`
    }
    return inviteeName.full_name.value
  }

  const buildFormData = () => {
    const invitee = {}

    if (inviteeNameFormat === 'separated') {
      invitee.first_name = inviteeName.first_name
      invitee.last_name = inviteeName.last_name
    } else {
      invitee.full_name = inviteeName.full_name
    }

    if (smsReminderEnabled) {
      invitee.phone_number = inviteePhoneNumber
    }

    if (externalCommunicationConsent) {
      invitee.external_communication_consent = externalCommunicationConsent
    }

    invitee.email = inviteeEmail

    return {
      invitee,
      inviteeGuests,
      event,
      event_fields: eventCustomFields,
      recaptchaVerifyCallback: () => recaptchaTokenV3,
      recaptcha_token_version_2: recaptchaTokenV2,
      client: currentClient,
      bookingFlow,
      creatorUuid,
      couponData,
    }
  }

  const buildStripeRedirectSnapshot = (formData) => ({
    invitee: formData.invitee,
    invitee_guests: formData.inviteeGuests,
    event: formData.event,
    event_fields: formData.event_fields
  })

  const dispatchOnSubmit = async (formData, requestId) => {
    if (isPoll) {
      return dispatch(submitPollVotes(spotStartTimes, formData, requestId))
    }

    if (saveFormDataForStripeRedirect) {
      const paymentIntentId = stripeClientSecret?.value?.split('_secret')[0]
      if (!paymentIntentId) {
        dispatch(setScheduleError({
          payload: {
            failure: {
              error: i18n.t('booking.error.default')
            }
          }
        }))
        return undefined
      }
      const snapshot = buildStripeRedirectSnapshot(formData)
      const saved = await dispatch(saveStripeRedirectSession(paymentIntentId, snapshot))
      if (!saved) return undefined
    }

    return dispatch(submitScheduling(spotStartTime, formData, paymentApi, currentInvitee, requestId, currentClient, asyncBookingEnabled))
  }

  const handleCloseRecaptchaPopup = () => {
    dismissRecaptchaPopup()
    closeRecaptchaModalAnalytic()
    dispatchRecaptchaInteractionAnalyticsCallback('failure')
  }

  const onSubmit = (e) => {
    e.preventDefault()

    const builder = asyncBookingEnabled ? buildRequestIdBase64 : buildRequestId
    let requestId = builder(
      requestKeySalt,
      spotStartTime,
      uuid,
      inviteeNameValue()
    )

    // Reuse stored requestId when resubmitting after recaptcha
    if (showRecaptchaChallenge && recaptchaRequestId) {
      requestId = recaptchaRequestId
    }
    unfinishedRecaptchaModalAnalytic()

    const formData = buildFormData()
    return dispatchOnSubmit(formData, requestId)
  }

  useEffect(() => {
    const requestIdFromUrl = getBookingRequestId()
    if (requestIdFromUrl && !hasResumedAsyncBooking.current) {
      hasResumedAsyncBooking.current = true

      // Restore UI from saved formData snapshot
      try {
        const raw = sessionStorage.getItem(`ab_form_${requestIdFromUrl}`)
        if (raw) {
          const { formData } = JSON.parse(raw) || {}
          if (formData) {
            const inv = formData.invitee || {}
            if (inviteeNameFormat === 'separated') {
              setInviteeName((prev) => ({
                ...prev,
                first_name: inv.first_name || prev.first_name,
                last_name: inv.last_name || prev.last_name,
                full_name: { value: null }
              }))
            } else {
              setInviteeName((prev) => ({
                ...prev,
                full_name: inv.full_name || prev.full_name,
                first_name: { value: null },
                last_name: { value: null }
              }))
            }
            if (inv.email) setInviteeEmail(inv.email)
            if (Array.isArray(formData.inviteeGuests)) setInviteeGuests(formData.inviteeGuests)
            if (inv.phone_number) setInviteePhoneNumber(inv.phone_number)
            if (formData.event) setEvent(formData.event)
            if (Array.isArray(formData.event_fields) && formData.event_fields.length > 0) {
              if (eventCustomFields.length === 0) {
                dispatch(setInitialCustomField(formData.event_fields))
              } else {
                formData.event_fields.forEach((field, index) => {
                  dispatch(updateCustomField({ index, props: { value: field.value } }))
                })
              }
            }
          }
        }
      } catch (_) { /* noop */ }

      // Resume polling if the page was refreshed during async booking
      dispatch(resumeAsyncBooking(requestIdFromUrl))
    }
  }, [dispatch, eventCustomFields.length, inviteeNameFormat])

  const renderLocation = () => {
    if (isWithoutLocation(ignoreLocationConfigurations, locationConfigurations)) {
      return null
    }

    return (isMultipleLocation(locationConfigurations)
      ? <MultipleLocation /> : <SingleLocation />)
  }

  const setToken = (token) => {
    setRecaptchaTokenV2(token)
  }

  const recaptchaPopupSegmentAnalyticsCallback = () => {
    Segment.track('abuse_prevention.invitee.recaptcha_challenge', {
      publisher_id: profile?.id
    })
  }

  useEffect(() => {
    if (automaticallySubmitForStripeRedirect && submitButton.current) {
      submitButton.current.click()
    }
  }, [automaticallySubmitForStripeRedirect])

  const showPaymentForm = isPaymentCheckoutAvailable(eventType) && !purchasedPackage

  return (
    <form
      autoComplete="off"
      autoCorrect="off"
      noValidate
      onSubmit={onSubmit}
    >
      <Container style={embedCustomColors ? embedCustomColorFormSurfaceStyle : undefined}>
        <Recaptcha
          action="scheduling"
          setVerifyCallback={recaptchaHandler}
          onError={recaptchaEnqueueAnalytic}
        />
        <InviteeInputs
          inviteeName={inviteeName}
          onInviteeNameChange={onInviteeNameChange}
          inviteeEmail={inviteeEmail}
          onInviteEmailChange={onInviteEmailChange}
        />
        {guestsAllowed && (
          <GuestsField
            guests={inviteeGuests}
            onAdd={onGuestsAdd}
            onRemove={onGuestsRemove}
            onUpdate={onGuestsUpdate}
            errors={guestErrors}
            invalidEmails={invalidGuestEmails}
            dismissError={dismissGuestsErrors}
          />
        )}
        {renderLocation()}
        {(isPoll || (customFields.length === 0)) ? null : <CustomFields fields={eventCustomFields} onChange={onCustomFieldChange} />}
        {smsReminderEnabled && (
          <TextReminder
            value={inviteePhoneNumber.value}
            required={inviteePhoneNumber.required}
            onChange={onTextReminderChange}
          />
        )}
        {showPaymentForm && (
          <BookingField>
            <Payment
              registerApi={registerApi}
              inviteeEmail={inviteeEmail.value}
              onPaypalAuthorized={() => submitButton.current?.click()}
            />
          </BookingField>
        )}
        {isSubmittingIntercomSheet && <IntercomConfirmation />}
        {(!isGdprApplied || !showMailchimpInviteeConsent) ? null : (
          <ExternalCommunicationConsent
            value={externalCommunicationConsent}
            onChange={setExternalCommunicationConsent}
          />
        )}
        {showRecaptchaChallenge && (
          <RecaptchaPopup
            onClose={handleCloseRecaptchaPopup}
            onConfirm={onSubmit}
            setVerifyCallback={setToken}
            loading={status === 'submitting'}
            challengeFailed={failedRecaptchaChallenge}
            segmentAnalyticsCallback={recaptchaPopupSegmentAnalyticsCallback}
          />
        )}
      </Container>
      {showLegalCopy && <LegalCopy />}
      <Button
        type="submit"
        size="large"
        loading={status === 'submitting'}
        ref={submitButton}
      >
        {isPoll
          ? i18n.t('booking.templates.poll.share_times_button')
          : i18n.t('booking.templates.schedule_event')}
      </Button>
    </form>
  )
}

export default SchedulingForm
